import { useState, useEffect, useRef, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, ChevronDown } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

// Top-level nav items — make sure each key exists in `src/i18n/routes.ts`
// and has a `nav.*` translation in the locales files.
const navItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'home', labelKey: 'nav.home' },
]

// Stand-alone contact link rendered after the e Tas group.
const contactItem: { key: RouteKey; labelKey: string } = {
  key: 'contacts',
  labelKey: 'nav.contacts',
}

// Items grouped under the "e Tas" dropdown.
const etasLinks: { key: RouteKey; labelKey: string }[] = [
  { key: 'maia', labelKey: 'nav.maiaShort' },
  { key: 'bar', labelKey: 'nav.barShort' },
  { key: 'pizza', labelKey: 'nav.pizzaShort' },
]

function NavLink({
  item,
  isTransparent,
  isLightTop,
  currentSlug,
}: {
  item: { key: RouteKey; labelKey: string }
  isTransparent: boolean
  isLightTop: boolean
  currentSlug: string
}) {
  const { t } = useTranslation()
  const routePath = localizedRoutes[item.key]
  const isActive =
    routePath.it === currentSlug ||
    routePath.en === currentSlug ||
    (item.key === 'home' && currentSlug === '')

  return (
    <LocalizedLink
      to={item.key}
      className={cn(
        'group relative px-3 py-2 text-sm font-medium rounded-md transition-colors',
        isTransparent
          ? isLightTop
            ? 'text-ink/90 hover:text-ink hover:bg-ink/10'
            : 'text-white/90 hover:text-white hover:bg-white/10'
          : 'text-gray-900 hover:bg-gray-100',
        isActive && 'font-semibold'
      )}
    >
      {t(item.labelKey)}
      <span
        className={cn(
          'absolute bottom-0 left-3 right-3 h-0.5 rounded-full transition-all',
          isActive
            ? 'scale-x-100 opacity-100'
            : 'scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100',
          isTransparent
            ? isLightTop
              ? 'bg-ink'
              : 'bg-white'
            : 'bg-gray-900'
        )}
      />
    </LocalizedLink>
  )
}

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isEtasOpen, setIsEtasOpen] = useState(false)
  const [isMobileEtasOpen, setIsMobileEtasOpen] = useState(false)
  const etasRef = useRef<HTMLDivElement>(null)

  // Current slug (second path segment after the language prefix)
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const currentSlug = pathSegments[1] || ''

  // Pages with a light hero that need dark text on the transparent header
  const pagesWithLightTop: string[] = ['', 'bar-e-tas']
  const isLightTop = pagesWithLightTop.includes(currentSlug)

  // Book button color matches the current venue page
  const pageButton = useMemo(() => {
    switch (currentSlug) {
      case 'bar-e-tas':
        return {
          solid: 'bg-bar text-ink hover:bg-bar-dark',
          transparent: 'bg-white text-bar-dark hover:bg-cream',
        }
      case 'pizza-e-tas':
        return {
          solid: 'bg-pizza text-white hover:bg-pizza-dark',
          transparent: 'bg-white text-pizza hover:bg-cream',
        }
      case 'maia-e-tas':
      case 'contatti':
      case 'contacts':
        return {
          solid: 'bg-maia text-white hover:bg-maia-dark',
          transparent: 'bg-white text-maia hover:bg-cream',
        }
      default:
        return {
          solid: 'bg-maia text-white hover:bg-maia-dark',
          transparent: 'bg-maia text-white hover:bg-maia-dark',
        }
    }
  }, [currentSlug])

  // Detect scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu and dropdowns on route change
  useEffect(() => {
    setIsMobileOpen(false)
    setIsEtasOpen(false)
    setIsMobileEtasOpen(false)
  }, [location.pathname])

  // Close e Tas desktop dropdown on outside click or Escape
  useEffect(() => {
    if (!isEtasOpen) return

    const handleClick = (event: MouseEvent) => {
      if (etasRef.current && !etasRef.current.contains(event.target as Node)) {
        setIsEtasOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsEtasOpen(false)
    }

    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [isEtasOpen])

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileOpen])

  const isTransparent = !isScrolled && !isMobileOpen

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          isTransparent ? 'bg-transparent' : 'bg-white/95 backdrop-blur-md shadow-md'
        )}
      >
      <nav
        aria-label={t('aria.mainNavigation', 'Main navigation')}
        className="relative mx-auto flex max-w-7xl items-center px-4 sm:px-6 lg:px-8 h-16 md:h-20"
      >
        {/* Logo */}
        <LocalizedLink
          to="home"
          className="absolute left-4 top-1/2 -translate-y-1/2 sm:left-6 lg:left-8 flex-shrink-0"
        >
          <span
            className={cn(
              'text-xl md:text-2xl font-bold tracking-tight transition-colors',
              isTransparent
                ? isLightTop
                  ? 'text-ink'
                  : 'text-white'
                : 'text-gray-900'
            )}
          >
            {t('brand.logo')}
          </span>
        </LocalizedLink>

        {/* Desktop Navigation — centered */}
        <div className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 lg:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              item={item}
              isTransparent={isTransparent}
              isLightTop={isLightTop}
              currentSlug={currentSlug}
            />
          ))}

          {/* e Tas dropdown */}
          <div className="relative" ref={etasRef}>
            <button
              type="button"
              id="etas-menu-button"
              onClick={() => setIsEtasOpen((open) => !open)}
              aria-expanded={isEtasOpen}
              aria-haspopup="menu"
              aria-controls="etas-menu"
              className={cn(
                'group relative flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                isTransparent
                  ? isLightTop
                    ? 'text-ink/90 hover:text-ink hover:bg-ink/10'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                  : 'text-gray-900 hover:bg-gray-100'
              )}
            >
              {t('nav.etas')}
              <ChevronDown
                className={cn(
                  'h-4 w-4 transition-transform duration-200',
                  isEtasOpen && 'rotate-180'
                )}
                aria-hidden="true"
              />
            </button>
            {isEtasOpen && (
              <div
                id="etas-menu"
                role="menu"
                aria-label={t('aria.etasMenu')}
                className="absolute top-full left-1/2 mt-2 w-52 -translate-x-1/2 rounded-xl bg-white py-2 shadow-lg ring-1 ring-black/5"
              >
                {etasLinks.map(({ key, labelKey }) => {
                  const routePath = localizedRoutes[key]
                  const isActive =
                    routePath.it === currentSlug || routePath.en === currentSlug

                  return (
                    <LocalizedLink
                      key={key}
                      to={key}
                      role="menuitem"
                      onClick={() => setIsEtasOpen(false)}
                      className={cn(
                        'block px-4 py-2 text-sm transition-colors',
                        isActive
                          ? 'bg-gray-100 font-semibold text-gray-900'
                          : 'text-gray-700 hover:bg-gray-50'
                      )}
                    >
                      {t(labelKey)}
                    </LocalizedLink>
                  )
                })}
              </div>
            )}
          </div>

          <NavLink
            item={contactItem}
            isTransparent={isTransparent}
            isLightTop={isLightTop}
            currentSlug={currentSlug}
          />
        </div>

        {/* Actions — desktop language/book + mobile toggle */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 sm:right-6 lg:right-8 flex items-center gap-3">
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSelector variant="desktop" isScrolled={!isTransparent} isLightTop={isLightTop} />
            <LocalizedLink
              to="book"
              className={cn(
                'rounded-full px-4 py-2 text-sm font-semibold transition-colors',
                isTransparent ? pageButton.transparent : pageButton.solid
              )}
            >
              {t('nav.book')}
            </LocalizedLink>
          </div>
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={cn(
              'lg:hidden p-2 rounded-md transition-colors',
              isTransparent
                ? isLightTop
                  ? 'text-ink hover:bg-ink/10'
                  : 'text-white hover:bg-white/10'
                : 'text-gray-900 hover:bg-gray-100'
            )}
            aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
            aria-expanded={isMobileOpen}
          >
            {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      </header>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 top-16 md:top-20 bg-white z-40 overflow-y-auto"
          >
            <nav
              className="flex flex-col py-4"
              aria-label={t('aria.mobileNavigation', 'Mobile navigation')}
            >
              {navItems.map(({ key, labelKey }) => {
                const routePath = localizedRoutes[key]
                const isActive =
                  routePath.it === currentSlug ||
                  routePath.en === currentSlug ||
                  (key === 'home' && currentSlug === '')

                return (
                  <LocalizedLink
                    key={key}
                    to={key}
                    className={cn(
                      'px-6 py-3 text-lg font-medium transition-colors',
                      isActive
                        ? 'text-gray-900 bg-gray-100 border-l-4 border-gray-900'
                        : 'text-gray-900 hover:bg-gray-50'
                    )}
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                )
              })}

              {/* Mobile e Tas accordion */}
              <div className="border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsMobileEtasOpen((open) => !open)}
                  aria-expanded={isMobileEtasOpen}
                  className="flex w-full items-center justify-between px-6 py-3 text-lg font-medium text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  {t('nav.etas')}
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 transition-transform duration-200',
                      isMobileEtasOpen && 'rotate-180'
                    )}
                    aria-hidden="true"
                  />
                </button>
                {isMobileEtasOpen && (
                  <div className="bg-gray-50">
                    {etasLinks.map(({ key, labelKey }) => {
                      const routePath = localizedRoutes[key]
                      const isActive =
                        routePath.it === currentSlug || routePath.en === currentSlug

                      return (
                        <LocalizedLink
                          key={key}
                          to={key}
                          className={cn(
                            'block pl-10 pr-6 py-3 text-base transition-colors',
                            isActive
                              ? 'bg-gray-100 font-semibold text-gray-900 border-l-4 border-gray-900'
                              : 'text-gray-700 hover:bg-gray-100'
                          )}
                        >
                          {t(labelKey)}
                        </LocalizedLink>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Mobile Contacts */}
              <LocalizedLink
                to={contactItem.key}
                className={cn(
                  'px-6 py-3 text-lg font-medium transition-colors border-t border-gray-100',
                  (localizedRoutes.contacts.it === currentSlug ||
                    localizedRoutes.contacts.en === currentSlug)
                    ? 'text-gray-900 bg-gray-100 border-l-4 border-gray-900'
                    : 'text-gray-900 hover:bg-gray-50'
                )}
              >
                {t(contactItem.labelKey)}
              </LocalizedLink>
              <div className="border-t border-gray-200 mt-4 pt-4 px-6 space-y-2">
                <LocalizedLink
                  to="book"
                  className="flex w-full items-center justify-center rounded-full bg-maia px-4 py-3 text-center font-semibold text-white transition-colors hover:bg-maia-dark"
                >
                  {t('nav.book')}
                </LocalizedLink>
                <LanguageSelector variant="mobile" />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
